import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ConsentModalOptions, PreferencesModalOptions, Section, Translation } from 'vanilla-cookieconsent';
import { ConsentGroupName, ConsentServiceName, ICompanyInfo, IConsentService } from '../../models';
import { ISOLanguage } from '../../models/applanguage/app-language';

export type ConsentInfo = Record<ConsentServiceName, IConsentService>;

@Injectable({
  providedIn: 'root'
})
export class ConsentService {
  // Services
  private readonly translateService: TranslateService = inject(TranslateService);

  // Properties & Signals
  /** The consent information containing all services and their details */
  public consentInfo: WritableSignal<ConsentInfo | undefined> = signal(undefined);
  /** The link targets for privacy policy, imprint, etc. */
  public linkTargets: WritableSignal<Record<string, string> | undefined> = signal(undefined);
  /** The company information to be displayed in the consent modal */
  public companyInfo: WritableSignal<ICompanyInfo | undefined> = signal(undefined);
  /** The detailed company information formatted for display */
  private readonly companyDetails: Signal<string> = computed(() => {
    const info = this.companyInfo();
    if (!info) return this.translateService.instant('cookiebanner.companyInfoNotAvailable');
    const street = info.address?.street ?? info.street;
    const houseNumber = info.address?.houseNumber ?? info.houseNumber;
    const zip = info.address?.zip ?? info.zip;
    const city = info.address?.city ?? info.city;
    return `${info.contact?.company}<br>${street} ${houseNumber}<br>${zip} ${city}`;
  });

  // Getters & Setters
  /** The translation for the footer */
  get footerTranslation(): string {
    return `
      <a href="${this.linkTargets()?.['privacy']}">${this.translateService.instant('cookiebanner.privacy')}</a>\n
      <a href="${this.linkTargets()?.['imprint']}">${this.translateService.instant('cookiebanner.imprint')}</a>
    `;
  }
  /** The combined basic translations for the modal options */
  get basicTranslation(): ConsentModalOptions | PreferencesModalOptions {
    return {
      title: this.translateService.instant('cookiebanner.title'),
      acceptAllBtn: this.translateService.instant('cookiebanner.acceptAllBtn'),
      acceptNecessaryBtn: this.translateService.instant('cookiebanner.acceptNecessaryBtn'),
      showPreferencesBtn: this.translateService.instant('cookiebanner.showPreferencesBtn'),
      footer: this.footerTranslation
    };
  }
  /** The translation for the ifram information (if used by site) */
  get iFrameTranslation(): string {
    const iFrameEnabled = !!this.getService('iframe');
    if (iFrameEnabled) return `<br><b class="font-bold">${this.translateService.instant('cookiebanner.marketingSection.iFrame')}</b>`;
    return '';
  }
  /** The translations of the sections (necessary, functionality, marketing) incl. informational texts shown as sections */
  get sectionsTranslations(): Section[] {
    const sections: Section[] = [
      {
        description: this.translateService.instant('cookiebanner.description')
      },
      {
        title: `${this.translateService.instant('cookiebanner.requiredSection.title')} <span class="pm__badge">${this.translateService.instant('cookiebanner.requiredSection.badge')}</span>`,
        description: this.translateService.instant('cookiebanner.requiredSection.description'),
        linkedCategory: 'necessary'
      }
    ];
    if (this.getServices('functionality')) {
      sections.push({
        title: this.translateService.instant('cookiebanner.functionalSection.title'),
        description: this.translateService.instant('cookiebanner.functionalSection.description'),
        linkedCategory: 'functionality'
      });
    }
    if (this.getServices('marketing')) {
      sections.push({
        title: this.translateService.instant('cookiebanner.marketingSection.title'),
        description: this.translateService.instant('cookiebanner.marketingSection.description') + this.iFrameTranslation,
        linkedCategory: 'marketing'
      });
    }
    sections.push({
      title: this.translateService.instant('cookiebanner.workingCompany'),
      description: this.companyDetails()
    });
    return sections;
  }
  /** Default language for the consent tool */
  get defaultLanguage(): string {
    return this.translateService.getCurrentLang() || 'en';
  }

  // Methods
  /**
   * Returns the translations for the cookie banner in the current language.
   * @returns The translations object for the cookie banner.
   */
  getTranslations(): Translation {
    return {
      consentModal: {
        ...this.basicTranslation,
        description: this.translateService.instant('cookiebanner.description'),
        closeIconLabel: this.translateService.instant('cookiebanner.closeIconLabel'),
        footer: this.footerTranslation
      },
      preferencesModal: {
        ...this.basicTranslation,
        closeIconLabel: this.translateService.instant('cookiebanner.closeIconLabel'),
        serviceCounterLabel: this.translateService.instant('cookiebanner.serviceCounterLabel'),
        savePreferencesBtn: this.translateService.instant('cookiebanner.savePreferencesBtn'),
        sections: this.sectionsTranslations
      }
    } as Translation;
  }

  /**
   * Returns translations for all supported language codes (short and full locale forms).
   * Vanilla-cookieconsent requires exact key matches for browser-detected locales like 'de-DE'.
   * @returns A record mapping each language code to its translation object.
   */
  getAllTranslations(): Record<ISOLanguage, Translation> {
    const translations = this.getTranslations();
    return {
      'en': translations,
      'en-US': translations,
      'de': translations,
      'de-DE': translations,
      'de-CH': translations,
      'de-AT': translations,
      'nl': translations,
      'nl-NL': translations,
      'fr': translations,
      'fr-FR': translations
    };
  }

  /**
   * Resolves the id a service is registered under inside vanilla-cookieconsent.
   * The backend guards every tracking snippet it ships in `metaData.headerJavaScript`
   * with `if (hasCookie('<cookie>'))`, and `hasCookie()` (consent.js) matches against the
   * accepted *service names* stored in the `cc_cookie`. The id must therefore be the
   * backend's `cookie` value — for several services (matomo → matomostatistic,
   * facebook → fbpixel, bing → binguet, gads → googleads, …) it differs from the
   * consentInfo key. Falls back to the key when no cookie is delivered.
   * @param name The consentInfo key of the service.
   * @returns The service id to pass to vanilla-cookieconsent.
   */
  getServiceId(name: ConsentServiceName): string {
    return this.consentInfo()?.[name]?.cookie || name;
  }

  /**
   * Gets the service with the given name from the list of services.
   * @param name The name of the service to retrieve.
   * @returns The service object if found, otherwise undefined.
   */
  getService(name: ConsentServiceName): CookieConsent.Service | undefined {
    const services = this.getServices();
    if (!services) return undefined;
    return services[this.getServiceId(name)];
  }

  /**
   * Builds all categories for the Cookie Consent tool.
   * @returns the categories object containing all necessary, functionality, and marketing categories.
   */
  getCategories(): Record<string, CookieConsent.Category> {
    const functionalityServices = this.getServices('functionality');
    const marketingServices = this.getServices('marketing');
    const categoriesObj: Record<string, CookieConsent.Category> = {
      necessary: {
        enabled: true,
        readOnly: true,
        services: this.getServices('necessary')
      }
    };
    if (functionalityServices) {
      categoriesObj['functionality'] = {
        enabled: false,
        readOnly: false,
        autoClear: {
          cookies: [{ name: /^(__cfduid|_p_hfp_client_id|session_id)/ }]
        },
        services: this.getServices('functionality')
      };
    }
    if (marketingServices) {
      categoriesObj['marketing'] = {
        enabled: false,
        readOnly: false,
        autoClear: {
          cookies: [{ name: /^(__cfduid|_p_hfp_client_id|session_id)/ }]
        },
        services: this.getServices('marketing')
      };
    }
    return categoriesObj;
  }

  /**
   * Generates a list of services based on the provided consent group.
   * The keys are the backend cookie names (see getServiceId()), not the consentInfo keys.
   * @param group The consent group to filter services by (e.g., 'necessary', 'functionality', 'marketing').
   * @returns An array of services that belong to the specified consent group.
   */
  private getServices(group?: ConsentGroupName): Record<string, CookieConsent.Service> | undefined {
    const consentInfo = this.consentInfo();
    if (!consentInfo) return;
    const services: Record<string, CookieConsent.Service> = {};
    Object.entries(consentInfo).forEach(([key, value]) => {
      if (!group || value.group === group) {
        // Register under the backend's cookie name, not the consentInfo key — see getServiceId().
        services[value.cookie || key] = {
          label: value.description
        };
      }
    });
    return Object.keys(services).length > 0 ? services : undefined;
  }
}
